import { TWebsiteBuilderElement, websiteBuilderElementValid } from "../element/websiteBuilderElement";
import { isWebsiteBuilderFlex, IWebsiteBuilderFlex, websiteBuilderNormalizeFlexSettings } from "./websiteBuilderFlex";
import { isWebsiteBuilderGrid, IWebsiteBuilderGrid, websiteBuilderNormalizeGridSettings } from "./websiteBuilderGrid";
import { isWebsiteBuilderForm, IWebsiteBuilderForm, websiteBuilderNormalizeFormSettings } from "./websiteBuilderForm";
import { isWebsiteBuilderTable, IWebsiteBuilderTable, websiteBuilderNormalizeTableSettings } from "./websiteBuilderTable";
import { TWebsiteBuilderHideableNodeVisibility, websiteBuilderNormalizeHideableNodeVisibility } from "../websiteBuilderHideableNode";
import { TWebsiteBuilderSectionEntityLayout, IWebsiteBuilderSectionEntityLayoutData, TWebsiteBuilderSectionEntityHasStretch, websiteBuilderNormalizeSectionEntityStretch, websiteBuilderNormalizeSectionEntityLayout } from "../websiteBuilderSectionEntity";
import { IWebsiteBuilderNodeBase, TWebsiteBuilderNode } from "../websiteBuilderNode";
import { IWebsiteBuilderGridCell, websiteBuilderGenerateDefaultGridCell, websiteBuilderGridCellValid } from "../gridCell/websiteBuilderGridCell";
import { TWebsiteBuilderNormalizeTraverse } from "../../schema/normalization/websiteBuilderNormalizeEditor";
import { isWebsiteBuilderSlideshow, IWebsiteBuilderSlideshow, websiteBuilderNormalizeSlideshowSettings } from "./websiteBuilderSlideshow";
import { isWebsiteBuilderBox, IWebsiteBuilderBox, websiteBuilderNormalizeBoxSettings } from "./websiteBuilderBox";
import { IWebsiteBuilderTab, websiteBuilderTabValid } from "../tab/websiteBuilderTab";
import { isWebsiteBuilderTabset, IWebsiteBuilderTabset, websiteBuilderNormalizeTabsetSettings } from "./websiteBuilderTabset";
import { isWebsiteBuilderMarquee, IWebsiteBuilderMarquee, websiteBuilderNormalizeMarqueeSettings } from "./websiteBuilderMarquee";

export const WEBSITE_BUILDER_CONTAINER_CLASS_NAME = "website-builder-container";

export interface IWebsiteBuilderContainerDataBase {
  type: string;
  layout: TWebsiteBuilderSectionEntityLayout;
  visibility: TWebsiteBuilderHideableNodeVisibility;
  zIndex: number;
  settings: unknown;
}

type TWebsiteBuilderContainerChild = TWebsiteBuilderElement | IWebsiteBuilderGridCell | IWebsiteBuilderTab;

export interface IWebsiteBuilderContainerBase<TChildren extends TWebsiteBuilderContainerChild[]> extends IWebsiteBuilderNodeBase {
  type: "container";
  container: IWebsiteBuilderContainerDataBase;
  children: TChildren;
}

type TWebsiteBuilderLayoutContainer =
  | IWebsiteBuilderBox
  | IWebsiteBuilderFlex
  | IWebsiteBuilderGrid
  | IWebsiteBuilderTabset;

type TWebsiteBuilderConstructedContainer =
  | IWebsiteBuilderForm
  | IWebsiteBuilderMarquee
  | IWebsiteBuilderSlideshow
  | IWebsiteBuilderTable;

export type TWebsiteBuilderContainer = TWebsiteBuilderLayoutContainer | TWebsiteBuilderConstructedContainer;

export type TWebsiteBuilderContainerData = TWebsiteBuilderContainer["container"];
export type TWebsiteBuilderContainerType = TWebsiteBuilderContainerData["type"];
export type TWebsiteBuilderContainerSettings = TWebsiteBuilderContainerData["settings"];

export type TWebsiteBuilderContainerWithChildren = Exclude<TWebsiteBuilderContainer, TWebsiteBuilderConstructedContainer>;

type TWebsiteBuilderContainerWithBuilder = IWebsiteBuilderForm | IWebsiteBuilderTable;

type TWebsiteBuilderStretchContainerData = TWebsiteBuilderSectionEntityHasStretch<TWebsiteBuilderContainerData>;
type TWebsiteBuilderStretchContainerType = TWebsiteBuilderStretchContainerData["type"];
const stretchContainerTypes: TWebsiteBuilderStretchContainerType[] = ["flex", "marquee", "slideshow"];

export function isWebsiteBuilderStretchContainerData(
  container: TWebsiteBuilderContainerData,
): container is TWebsiteBuilderStretchContainerData {
  return stretchContainerTypes.some(stretchContainerType => stretchContainerType === container.type);
}

export function isWebsiteBuilderLayoutContainer(node: TWebsiteBuilderNode): node is TWebsiteBuilderLayoutContainer {
  return node?.type === "container" && (
    node.container.type === "box" ||
    node.container.type === "flex" ||
    node.container.type === "grid" ||
    node.container.type === "tabset"
  );
}

function isWebsiteBuilderConstructedContainer(node: TWebsiteBuilderNode): node is TWebsiteBuilderConstructedContainer {
  return node?.type === "container" && (
    node.container.type === "form" ||
    node.container.type === "marquee" ||
    node.container.type === "slideshow" ||
    node.container.type === "table"
  );
}

export function isWebsiteBuilderContainer(node: any): node is TWebsiteBuilderContainer {
  return node?.type === "container";
}

export function isWebsiteBuilderContainerWithChildren(node: TWebsiteBuilderNode): node is TWebsiteBuilderContainerWithChildren {
  return node?.type === "container" && !isWebsiteBuilderConstructedContainer(node);
}

export function isWebsiteBuilderContainerWithBuilder(node: TWebsiteBuilderNode): node is TWebsiteBuilderContainerWithBuilder {
  return node?.type === "container" && (
    node.container.type === "form" ||
    node.container.type === "table"
  );
}

export function websiteBuilderContainerValid(node: any): node is TWebsiteBuilderContainer {
  const partialNode: Partial<TWebsiteBuilderContainer> = node;
  
  const isValidLayoutData = (data: IWebsiteBuilderSectionEntityLayoutData) => {
    return (
      data?.position?.x != null &&
      data?.position?.y != null &&
      data?.dimensions?.width != null &&
      data?.dimensions?.height != null
    );
  };

  return (
    partialNode != null &&
    partialNode.id != null &&
    partialNode.type === "container" &&
    partialNode.container != null &&
    (
      partialNode.container.type === "box" ||
      partialNode.container.type === "flex" ||
      partialNode.container.type === "form" ||
      partialNode.container.type === "grid" ||
      partialNode.container.type === "marquee" ||
      partialNode.container.type === "slideshow" ||
      partialNode.container.type === "table" ||
      partialNode.container.type === "tabset"
    ) &&
    isValidLayoutData(partialNode.container.layout?.desktop) &&
    isValidLayoutData(partialNode.container.layout?.tablet) &&
    isValidLayoutData(partialNode.container.layout?.mobile) &&
    partialNode.container.zIndex != null
  );
}

export function websiteBuilderNormalizeValidContainer(
  node: TWebsiteBuilderContainer,
  normalizeTraverse: TWebsiteBuilderNormalizeTraverse,
): TWebsiteBuilderContainer {
  const normalizedLayout = websiteBuilderNormalizeSectionEntityLayout(node.container.layout);
  const normalizedVisibility = websiteBuilderNormalizeHideableNodeVisibility(node.container.visibility);

  if (isWebsiteBuilderBox(node)) {
    const children = node.children?.filter(child => {
      return websiteBuilderElementValid(child);
    }) ?? [];

    const normalizedSettings = websiteBuilderNormalizeBoxSettings(node.container.settings);

    const normalizedBox: IWebsiteBuilderBox = {
      id: node.id,
      type: node.type,
      container: {
        type: node.container.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.container.zIndex,
        settings: normalizedSettings,
      },
      children: children.map(child => normalizeTraverse(child)),
    };

    return normalizedBox;
  } else if (isWebsiteBuilderFlex(node)) {
    const children = node.children?.filter(child => {
      return websiteBuilderElementValid(child);
    }) ?? [];

    const normalizedSettings = websiteBuilderNormalizeFlexSettings(node.container.settings);
    const normalizedStretch = websiteBuilderNormalizeSectionEntityStretch(node.container.stretch);

    const normalizedFlex: IWebsiteBuilderFlex = {
      id: node.id,
      type: node.type,
      container: {
        type: node.container.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.container.zIndex,
        settings: normalizedSettings,
        stretch: normalizedStretch,
      },
      children: children.map(child => normalizeTraverse(child)),
    };

    return normalizedFlex;
  } else if (isWebsiteBuilderGrid(node)) {
    const normalizedSettings = websiteBuilderNormalizeGridSettings(node.container.settings);

    const children = Array.from(
      { length: normalizedSettings.rows * normalizedSettings.columns },
      (_, index) => {
        const child = node.children?.[index];

        return websiteBuilderGridCellValid(child)
          ? child
          : websiteBuilderGenerateDefaultGridCell();
      },
    );

    const normalizedGrid: IWebsiteBuilderGrid = {
      id: node.id,
      type: node.type,
      container: {
        type: node.container.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.container.zIndex,
        settings: normalizedSettings,
      },
      children: children.map(child => normalizeTraverse(child)),
    };

    return normalizedGrid;
  } else if (isWebsiteBuilderMarquee(node)) {
    const normalizedSettings = websiteBuilderNormalizeMarqueeSettings(node.container.settings);
    const normalizedStretch = websiteBuilderNormalizeSectionEntityStretch(node.container.stretch);

    const normalizedMarquee: IWebsiteBuilderMarquee = {
      id: node.id,
      type: node.type,
      container: {
        type: node.container.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.container.zIndex,
        settings: normalizedSettings,
        stretch: normalizedStretch,
      },
      children: [],
    };

    return normalizedMarquee;
  } else if (isWebsiteBuilderForm(node)) {
    const normalizedSettings = websiteBuilderNormalizeFormSettings(node.container.settings);

    const normalizedForm: IWebsiteBuilderForm = {
      id: node.id,
      type: node.type,
      container: {
        type: node.container.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.container.zIndex,
        settings: normalizedSettings,
      },
      children: [],
    };

    return normalizedForm;
  } else if (isWebsiteBuilderSlideshow(node)) {
    const normalizedSettings = websiteBuilderNormalizeSlideshowSettings(node.container.settings);
    const normalizedStretch = websiteBuilderNormalizeSectionEntityStretch(node.container.stretch);

    const normalizedSlideshow: IWebsiteBuilderSlideshow = {
      id: node.id,
      type: node.type,
      container: {
        type: node.container.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.container.zIndex,
        settings: normalizedSettings,
        stretch: normalizedStretch,
      },
      children: [],
    };

    return normalizedSlideshow;
  } else if (isWebsiteBuilderTable(node)) {
    const normalizedSettings = websiteBuilderNormalizeTableSettings(node.container.settings);

    const normalizedTable: IWebsiteBuilderTable = {
      id: node.id,
      type: node.type,
      container: {
        type: node.container.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.container.zIndex,
        settings: normalizedSettings,
      },
      children: [],
    };

    return normalizedTable;
  } else if (isWebsiteBuilderTabset(node)) {
    const children = node.children?.filter(child => {
      return websiteBuilderTabValid(child);
    }) ?? [];

    const tabIds = children.map(child => child.id);
    const normalizedSettings = websiteBuilderNormalizeTabsetSettings(node.container.settings, tabIds);

    const normalizedTabset: IWebsiteBuilderTabset = {
      id: node.id,
      type: node.type,
      container: {
        type: node.container.type,
        layout: normalizedLayout,
        visibility: normalizedVisibility,
        zIndex: node.container.zIndex,
        settings: normalizedSettings,
      },
      children: children.map(child => normalizeTraverse(child)),
    };

    return normalizedTabset;
  } else {
    const _exhaustiveCheck: never = node;
    return _exhaustiveCheck;
  }
}